import React, { useState, useEffect } from "react";
import Layout from "../pages/Layout";
import Advertise from "../pages/Advertise";

const CashRunway = () => {
  // State variables for Cash Runway calculation
  const [totalCash, setTotalCash] = useState(0); // Total cash available
  const [monthlyBurnRate, setMonthlyBurnRate] = useState(0); // Monthly burn rate
  const [cashRunway, setCashRunway] = useState(0); // Calculated cash runway

  // Event handlers to update state values
  const handleTotalCashChange = (e) =>
    setTotalCash(parseFloat(e.target.value) || 0);
  const handleBurnRateChange = (e) =>
    setMonthlyBurnRate(parseFloat(e.target.value) || 0);

  // useEffect hook to calculate the Cash Runway
  useEffect(() => {
    if (monthlyBurnRate > 0) {
      const runway = totalCash / monthlyBurnRate;
      setCashRunway(runway);
    }

    // This will initialize or reinitialize all popovers on the page whenever the component renders
    const popoverTriggerList = [].slice.call(
      document.querySelectorAll('[data-bs-toggle="popover"]')
    );

    // Clean up any existing popover instances
    popoverTriggerList.forEach((popoverTriggerEl) => {
      const popover = window.bootstrap.Popover.getInstance(popoverTriggerEl); // Check if instance already exists
      if (popover) {
        popover.dispose(); // Dispose of existing popover
      }
    });

    // Re-initialize popovers
    popoverTriggerList.forEach((popoverTriggerEl) => {
      new window.bootstrap.Popover(popoverTriggerEl); // Re-initialize popovers
    });
  }, [totalCash, monthlyBurnRate]);

  return (
    <Layout title="Cash Runway Calculator" path="cash-runway">
      <section className="section-padding" id="cash-runway">
        <div className="container mt-5">
          <div className="row">
            {/* Cash Runway Form */}
            <div className="col-lg-9 col-md-8 mb-4">
              <div
                className="p-4 border rounded shadow"
                style={{ border: "1px solid black" }}
              >
                <h3 className="text-center mb-4">Cash Runway Calculator</h3>
                <div className="container">
                  <div className="row gy-3">
                    {/* Input for Total Cash */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="totalCash" className="form-label">
                        Total Cash
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Total Cash:</strong> The total cash available to your business or project."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="totalCash"
                        placeholder="Enter total cash available"
                        value={totalCash || ""}
                        onChange={handleTotalCashChange}
                      />
                    </div>

                    {/* Input for Monthly Burn Rate */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="monthlyBurnRate" className="form-label">
                        Monthly Burn Rate
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Monthly Burn Rate:</strong> The amount of cash your business spends monthly."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="monthlyBurnRate"
                        placeholder="Enter monthly burn rate"
                        value={monthlyBurnRate || ""}
                        onChange={handleBurnRateChange}
                      />
                    </div>
                  </div>

                  {/* Result Section */}
                  <div className="mt-4">
                    <div className="p-3 text-center">
                      <h6>
                        Cash Runway:{" "}
                        <span className="fw-bold ms-3">
                          {cashRunway === 0
                            ? "0"
                            : cashRunway.toLocaleString(undefined, {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}{" "}
                          months
                        </span>
                      </h6>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Advertisement Section */}
            <Advertise />
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="introduction-section" id="introduction">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="p-5">
                <h3 className="text-center mb-5">
                  Introduction to Cash Runway
                </h3>

                {/* What is Cash Runway? */}
                <h4 className="mt-3 mb-3">What is Cash Runway?</h4>
                <p>
                  **Cash Runway** refers to the amount of time a company can
                  operate before it runs out of cash. It is a critical metric
                  for startups and businesses to assess how long they can
                  continue to run their operations without additional funding or
                  revenue generation.
                </p>

                {/* Why Use the Cash Runway Calculator? */}
                <h4 className="mt-4 mb-3">
                  Why Use the Cash Runway Calculator?
                </h4>
                <p>
                  The Cash Runway Calculator helps businesses plan for the
                  future by assessing how long they can sustain operations at
                  their current rate of spending. It is particularly useful for
                  startups that may not yet be profitable or have inconsistent
                  cash flows.
                </p>

                {/* Benefits of Using the Cash Runway Calculator */}
                <h4 className="mt-4 mb-3">
                  Benefits of Using the Cash Runway Calculator
                </h4>
                <ul>
                  <li>
                    <strong>
                      Helps Startups and Businesses Plan for Sustainability:
                    </strong>{" "}
                    Provides insight into how long a company can operate with
                    the current cash reserves and spending rate.
                  </li>
                  <li>
                    <strong>
                      Supports Budgeting and Financing Strategies:
                    </strong>{" "}
                    Helps companies anticipate the need for new funding or
                    adjustments to spending.
                  </li>
                  <li>
                    <strong>Useful for Understanding Financial Health:</strong>{" "}
                    Offers a clear understanding of a company's cash position
                    and its ability to sustain operations.
                  </li>
                </ul>

                {/* Suitable For */}
                <h4 className="mt-4 mb-3">
                  Who Should Use the Cash Runway Calculator?
                </h4>
                <p>This calculator is suitable for:</p>
                <ul>
                  <li>
                    <strong>Startups:</strong> Helps new businesses assess their
                    runway and plan accordingly.
                  </li>
                  <li>
                    <strong>Corporate Finance Teams:</strong> Provides insights
                    for budgeting, financial planning, and forecasting.
                  </li>
                  <li>
                    <strong>Entrepreneurs:</strong> Useful for individual
                    business owners to understand the sustainability of their
                    operations.
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default CashRunway;
